// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, Trash2, TriangleAlert } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { type TaskAvailabilityEntry, useDeleteTaskAvailabilityMutation } from "@/api/hooks/use-tasks";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface DeleteTaskAvailabilityDialogProps {
  subtype: string;
  entry: TaskAvailabilityEntry | null;
  onClose: () => void;
}

/** Confirms removing one availability row — the task stops being offered on that scope. */
export function DeleteTaskAvailabilityDialog({ subtype, entry, onClose }: DeleteTaskAvailabilityDialogProps) {
  const { t } = useTranslation("back-office");
  const last = useRef(entry);
  if (entry) last.current = entry;
  const display = entry ?? last.current;
  const deleteAvailability = useDeleteTaskAvailabilityMutation(subtype);

  const scopeLabel = display?.storeName ?? display?.chainName ?? t("tasks.scope.global");

  async function handleDelete() {
    if (!display) return;
    try {
      await deleteAvailability.mutateAsync(display.availabilityId);
      toast.success(t("tasks.availability.dialog.removed"));
      onClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={!!entry} onOpenChange={() => onClose()}>
      <DialogContent
        id="dialog-delete-task-availability"
        className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col"
      >
        <div className="flex flex-row">
          <WizardPane variant="destructive" icon={Trash2} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{t("tasks.availability.dialog.removeTitle")}</DialogTitle>
              <DialogDescription asChild>
                <div>
                  <p>
                    {t("tasks.availability.dialog.removePrompt")}
                    <br />
                    <span className="font-semibold text-foreground">{scopeLabel}</span>?
                  </p>
                  <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                    <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                    <span className="text-sm">{t("tasks.availability.dialog.removeWarning")}</span>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteAvailability.isPending}>
            {deleteAvailability.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            {t("tasks.availability.dialog.removeConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
